/**
 * Ingest Executor
 *
 * Handles the 'upload-files' node execution.
 * Calls the ModernizeIT API Layer /ingest/upload endpoint.
 *
 * API Flow (WORKER MODE):
 * 1. POST /ingest/upload (multipart/form-data) → returns job_id with status QUEUED
 * 2. Poll GET /jobs/{job_id} → wait until status = 'completed'
 * 3. Extract results from job.output_data (no separate results endpoint)
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders } from './authHelper.js'

/**
 * Execute the ingest (upload-files) node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with jobs and source_hash
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing upload...')

  // Debug: Log what's in config
  console.log('[IngestExecutor] Config received:', {
    hasSourceHash: !!config.source_hash,
    hasIngestJobId: !!config.ingest_job_id,
    hasJobs: !!config.jobs,
    jobs: config.jobs,
    source_hash: config.source_hash,
    configKeys: Object.keys(config)
  })

  // Check if we have a source_hash from a previous run (saved flow scenario)
  // Try both direct properties and jobs object
  const sourceHash = config.source_hash || context.source_hash
  const ingestJobId = config.ingest_job_id || config.jobs?.ingest_job_id

  if (sourceHash && ingestJobId) {
    onProgress(10, 'Using previously uploaded file...')
    console.log('[IngestExecutor] Reusing existing upload:', {
      source_hash: sourceHash,
      job_id: ingestJobId
    })

    // Return the saved execution result without re-uploading
    onProgress(100, 'Complete')
    return {
      jobs: {
        ingest_job_id: ingestJobId,
      },
      source_hash: sourceHash,
      paths: config.paths || [],
      summary: config.summary || {},
      file_count: config.file_count || 0,
      files_processed: config.files_processed || [],
      duplicate: config.duplicate || false,
      artifacts_path: config.artifacts_path,
      file_catalog: config.file_catalog,
      classified_catalog: config.classified_catalog,
      type_mappings: config.type_mappings,
    }
  }

  // 1. Get staged files from config (set by FileUploadDropzone)
  const stagedFiles = config.stagedFiles
  if (!stagedFiles || stagedFiles.length === 0) {
    throw new Error('No file has been staged. Please add a file to the Upload Files node.')
  }

  // Get the first file (for now, we only support single file upload)
  const stagedFile = stagedFiles[0]
  if (!stagedFile.file) {
    throw new Error('Staged file is invalid. Please remove and re-add the file.')
  }

  // Check if file is actually a Blob/File object (not just metadata from saved flow)
  if (!(stagedFile.file instanceof Blob || stagedFile.file instanceof File)) {
    throw new Error('File data lost after loading saved flow. Please re-select the file in the Upload Files node.')
  }

  onProgress(5, 'File ready for upload...')

  // 2. Build form data for the API call
  onProgress(10, 'Uploading to API...')

  const formData = new FormData()
  formData.append('file', stagedFile.file, stagedFile.name)
  formData.append('scout_account_id', context.scout_account_id)
  formData.append('application_name', context.application_name)
  formData.append('user_id', context.user_id) // ADDED 2026-01-14: Required for per-user AWS credentials
  formData.append('source_lang', config.sourceLanguage || 'cobol')
  formData.append('target_lang', config.targetLanguage || 'java')
  formData.append('generate_type_mappings', String(config.generateTypeMappings ?? true))

  // Check if aborted before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 3. Submit the job
  let jobId
  let uploadData = {}
  try {
    const response = await fetch(`${apiBaseUrl}/ingest/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
      signal, // Pass abort signal to fetch
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`API error: ${response.status} - ${errorText}`)
    }

    uploadData = await response.json()
    jobId = uploadData.job_id

    if (!jobId) {
      throw new Error('API did not return a job_id')
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  onProgress(20, `Job started: ${jobId}`)

  // 4. Poll for completion using generic jobs endpoint
  console.log(`[IngestExecutor] Polling job ${jobId} until completed...`)

  const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
    maxWaitSeconds: 600, // 10 minutes
    pollInterval: 1000,  // 1 second
    onProgress: (progress, message) => {
      // Map polling progress from 20-95%
      const mappedProgress = 20 + (progress * 0.75)
      onProgress(mappedProgress, message)
    },
    signal
  })

  console.log('[IngestExecutor] Job completed, extracting results from output_data')
  onProgress(95, 'Extracting results...')

  // 5. Extract results from job.output_data (worker mode)
  const outputData = completedJob.output_data
  if (!outputData) {
    throw new Error('Job completed but has no output_data')
  }

  onProgress(100, 'Complete')

  // 6. Return context updates from job output_data
  return {
    jobs: {
      ingest_job_id: jobId,
    },
    // From output_data
    source_hash: outputData.source_hash,
    paths: outputData.paths || [],
    summary: outputData.summary || {},
    file_count: outputData.file_count || 0,
    files_processed: outputData.files_processed || [],
    duplicate: outputData.duplicate || false,
    artifacts_path: outputData.artifacts_path,
    file_catalog: outputData.file_catalog,
    classified_catalog: outputData.classified_catalog,
    type_mappings: outputData.type_mappings,
  }
}

export default {
  execute,
}
